import editorialStyle from "../../../../components/editorial/style.scss";
import style from "./style.scss";

import { Fragment } from "preact";
import Link, {
  Button,
  ButtonList,
  LargeViewOnly,
  StickyButtons,
  ButtonBar,
  affiliateMerchants,
} from "../../../../components/link";
import AffiliateDisclosure from "../../../../components/affiliate-disclosure";
import Center from "../../../../components/center";
import Editorial from "../../../../components/editorial";
import FixedTable from "../../../../components/fixed-table";
import Head from "../../../../components/head";
import Hero from "../../../../components/hero";
import ImageSet from "../../../../components/image-set";
import Image from "../../../../components/image";
import Author from "../../../../components/author";
import BluettiCouponCode from "../../components/coupon-code";
import NoWrap from "../../../../components/no-wrap";
import SlugHeading from "../../../../components/slug-heading";
import SlugLink from "../../../../components/slug-link";
import Accordion from "../../../../components/accordion";
import YouTube from "../../../../components/youtube";
import Warning from "../../../../components/warning";
import AccordionPreview from "../../../../components/accordion/preview";
import PhotoListItem from "../../../../components/photo-list-item";
import Tags from "../../../../components/tags";
import ReviewItem from "../../../../components/review-item";
import Questions from "../../../../components/questions";
import namedRoutes from "../../../../settings/routes";
import { lookupByUrl } from "../../../../settings/route-data-lookup";

let headerInfo = lookupByUrl(namedRoutes.bluetti.charger1.review);

const Page = () => (
  <div>
    <Head data={headerInfo} />
    <Hero
      positionX={25}
      positionY={73}
      background="/images/products/bluetti/charger-1/background.webp"
      titlePrefix="BLUETTI"
      title="Charger 1 Review"
      subTitle="560W Alternator Charger"
    />
    <Editorial>
      <Center>
        <Author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />

        <LargeViewOnly>
          <ButtonList size="medium">
            <Button
              size="medium"
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/bluetti-charger-1"
              icon="Bluetti"
            >
              View on BLUETTI
            </Button>
            <Button
              size="medium"
              affiliate="https://amzn.to/4msoVLk"
              href="https://www.amazon.com/dp/B0DGXYPMQ4/"
              secondary
              icon="Amazon"
            >
              View on Amazon
            </Button>
          </ButtonList>
        </LargeViewOnly>

        <p>
          The <strong>BLUETTI Charger 1</strong> is a compact DC-DC alternator
          charger designed to power vanlife, RV, and off‑grid power systems
          while driving. It gives you a way to quickly top off your power
          station on the road instead of relying solely on solar panels or slow
          12V car sockets. In this review, I break down what you need to know
          before buying, including setup details, third-party power station
          compatibility, and how it compares to the Charger 2.
        </p>

        <AffiliateDisclosure />

        {/* <ButtonList size="medium">
          <Button
            affiliateData={affiliateMerchants.bluetti}
            href="https://www.bluettipower.com/pages/charger-2-upgrade-campaign"
            icon="Bluetti"
          >
            Upgrade Bonus Info
          </Button>
        </ButtonList> */}

        <ImageSet
          size={4}
          shadow
          images={[
            {
              lg: "/images/products/bluetti/charger-1/1920/charger-1_9677.webp",
              sm: "/images/products/bluetti/charger-1/500/charger-1_9677.webp",
              alt: "BLUETTI Charger 1: cables plugged in",
            },
            {
              lg: "/images/products/bluetti/charger-1/1920/charger-1_9686.webp",
              sm: "/images/products/bluetti/charger-1/500/charger-1_9686.webp",
              alt: "BLUETTI Charger 1: left ports",
            },
            {
              lg: "/images/products/bluetti/charger-1/1920/charger-1_9685.webp",
              sm: "/images/products/bluetti/charger-1/500/charger-1_9685.webp",
              alt: "BLUETTI Charger 1: right ports",
            },
            {
              lg: "/images/products/bluetti/charger-1/1920/charger-1_9669.webp",
              sm: "/images/products/bluetti/charger-1/500/charger-1_9669.webp",
              alt: "BLUETTI Charger 1: accessories included in package (unboxing)",
            },
          ]}
        />

        <p class="caption-left">More photos and setup instructions below.</p>

        <Accordion
          items={[
            {
              heading: "Table of Contents",
              content: (
                <ol>
                  <li>
                    <SlugLink>Overview</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Considerations</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Detailed Comparison</SlugLink>
                    <ol class="upper-alpha">
                      <li>
                        <SlugLink>Full Specifications</SlugLink>
                      </li>
                    </ol>
                  </li>
                  <li>
                    <SlugLink>How to Install</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Setup Videos</SlugLink>
                  </li>
                  <li>
                    <SlugLink>App Control</SlugLink>
                  </li>
                  <li>
                    <SlugLink>My Verdict</SlugLink>
                  </li>
                </ol>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">Overview</SlugHeading>
        <ul>
          <li>
            <strong>Automatic Charging:</strong> Automatically controls charging
            using intelligent voltage detection so it only operates when the
            vehicle is running.
          </li>
          <li>
            <strong>Strong Performance:</strong> Delivers a 560W charging rate,
            though I occasionally see real-world peaks near 580W. It offers a
            solid mid-tier charging rate that keeps your power station
            recharging fast on the road.
          </li>
          <li>
            <strong>Compact Size:</strong> Takes up very little mounting area,
            making it easy to fit into your setup while maintaining the open
            airflow its cooling fan requires.
          </li>
          <li>
            <strong>Broad Compatibility:</strong> Basic charging and solar also
            work with many third‑party models from brands like EcoFlow and
            Jackery. This flexibility makes it a versatile option for
            mixed‑brand power setups.
          </li>
          <li>
            <strong>Smart App Control:</strong> Monitor energy flow and adjust
            charging settings via the BLUETTI app.
          </li>
          <li>
            <strong>Price:</strong> Competitively priced against{" "}
            <Link href={namedRoutes.alternatorChargers}>other options</Link> on
            the market.
          </li>
        </ul>
      </Center>
      <Center>
        <SlugHeading tag="h2">Considerations</SlugHeading>
        <ul>
          <li>
            <strong>Solar Input Conflict:</strong> Plugs directly into your
            power station's solar port, blocking simultaneous solar charging on
            single-input units unless you swap cables or add a switch. Even on
            units with dual physical ports, both jacks must be electronically
            independent to run solar alongside Charger 1. Charger 2 avoids solar
            port conflicts by connecting via BLUETTI’s expansion battery port
            and incorporating its own solar controller.
          </li>
          <li>
            <strong>Limited Power Output Controls:</strong> While the app
            supports voltage adjustment, it lacks a way to set a specific
            wattage. This makes it impossible to dial back charging power for
            lower‑capacity alternators without using Silent Charging mode, which
            throttles output automatically.
          </li>
          <li>
            <strong>Requires Accessory Cable:</strong> Uses a very short MC4
            output cable that typically requires an additional adapter cable
            (like MC4-to-XT60 or MC4-to-DC8020) to connect to your specific
            power station. Many power stations include this adapter, but you may
            need to source one separately depending on your model.
          </li>
          <li>
            <strong>Noise Levels:</strong> Unlike some competitor models that
            use fanless designs, Charger 1 relies on active fan cooling.
            However, it remains impressively quiet, especially when compared to
            Charger 2. For completely silent operation, the unit features a
            "Silent Charging" app toggle that throttles the charge rate down to
            roughly 300W to minimize heat and fan activity.
          </li>
          <li>
            <strong>DC Breaker:</strong> Uses a bulky 63A resettable breaker
            that doubles as a convenient manual power disconnect. However, it's
            located near the charger rather than near the vehicle battery, where
            standard DC wiring practices dictate fusing to protect the full
            cable run. Consider adding an aftermarket 70A fuse next to the
            battery for better protection.
          </li>
          <li>
            <strong>Limited Ingress Protection:</strong> With an IP20 rating,
            the Charger 1 offers no protection against water and minimal
            protection against dust. Keep this in mind if you plan to install it
            in dusty environments. Long-term dust buildup inside the unit could
            impact cooling performance compared to a sealed, fanless design.
          </li>
          <li>
            <strong>Altitude Limitation:</strong> The manual lists an operating
            altitude of up to 6,561 ft (2,000 m). If that limit is strict, it is
            a real constraint for people who camp or travel at higher
            elevations. BLUETTI should raise this limit in future hardware
            revisions or provide clearer guidance on what to expect above it.
          </li>
        </ul>
      </Center>
      <Center>
        <SlugHeading tag="h2">Detailed Comparison</SlugHeading>
        <p>
          BLUETTI{" "}
          <strong>
            <NoWrap>Charger 1</NoWrap>
          </strong>{" "}
          vs{" "}
          <strong>
            <NoWrap>Charger 2</NoWrap>
          </strong>{" "}
          comparison table:
        </p>

        <Fragment>
          <FixedTable>
            <table>
              <thead>
                <tr>
                  <th class={editorialStyle.sticky}>Model</th>
                  <th>Charger 1</th>
                  <th>Charger 2</th>
                </tr>
              </thead>
              <tbody>
                <tr class={editorialStyle.noHover}>
                  <th class={editorialStyle.sticky}>
                    <strong>Photo</strong>
                  </th>
                  <td>
                    <Image
                      src="/images/products/bluetti/charger-1/front.webp"
                      alt="BLUETTI Charger 1"
                      showAlt
                      maxHeight={100}
                    />
                  </td>
                  <td>
                    <Image
                      src="/images/products/bluetti/charger-2/front.webp"
                      alt="BLUETTI Charger 2"
                      showAlt
                      maxHeight={100}
                    />
                  </td>
                </tr>
                <tr class={editorialStyle.noHover}>
                  <th class={editorialStyle.sticky}>
                    <strong>Links</strong>
                  </th>
                  <td>
                    <ButtonBar>
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/bluetti-charger-1"
                        icon="Bluetti"
                      >
                        Store
                      </Button>
                      <Button
                        size="small"
                        external
                        href="https://s4.bluettipower.com/bluetti_lgf/support/2025/05/4a4f1b84-aa5a-4563-8e3f-28f5c3053576.pdf"
                        secondary
                      >
                        Manual
                      </Button>
                    </ButtonBar>
                  </td>
                  <td>
                    <ButtonBar>
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
                        icon="Bluetti"
                      >
                        Store
                      </Button>
                      <Button
                        size="small"
                        external
                        href="https://cdn.shopify.com/s/files/1/0550/5620/3836/files/CHARGER_2_User_Manual_EN.pdf?v=1768545688"
                        secondary
                      >
                        Manual
                      </Button>
                    </ButtonBar>
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Price</strong>
                  </th>
                  <td>
                    $249 <s>$399</s>
                  </td>
                  <td>
                    <NoWrap>
                      $499 <s>$599</s>
                    </NoWrap>
                  </td>
                </tr>
                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>Alternator Input</strong>
                  </th>
                  <td>560W</td>
                  <td>800W</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Solar Input</strong>
                  </th>
                  <td>--</td>
                  <td>13-50V⎓20A, 600W</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Power Station Port</strong>
                  </th>
                  <td>560W</td>
                  <td>1,200W</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Bidirectional Port</strong>
                  </th>
                  <td>--</td>
                  <td>
                    1,000W <sup>1</sup>
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>DC Hub Port</strong>
                  </th>
                  <td>--</td>
                  <td>
                    700W <sup>2</sup>
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Max Reverse Charge</strong>
                  </th>
                  <td>--</td>
                  <td>
                    800W <sup>1</sup>
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Battery Maintenance</strong>
                  </th>
                  <td>--</td>
                  <td>
                    100W <sup>1</sup>
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Signal Wire</strong> (D+)
                  </th>
                  <td>--</td>
                  <td>Add-on</td>
                </tr>
                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>Size</strong>
                  </th>
                  <td>
                    5.7 × 4.3 × 2.4 in
                    <br />
                    (145 × 110 × 60 mm)
                  </td>
                  <td>
                    10.4 × 6.7 × 2.7 in
                    <br />
                    (265 × 169 × 70 mm)
                  </td>
                </tr>
              </tbody>
            </table>
          </FixedTable>
          <p class="caption">
            <strong>1.</strong> Requires compatible BLUETTI product.{" "}
            <strong>2.</strong> Requires BLUETTI{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/dc-hub-for-charger-2"
            >
              DC Hub
            </Link>
            . Also requires a BLUETTI device connected to the bidirectional port
            to be active when vehicle is off.
          </p>
        </Fragment>

        <p>
          Charger 1 is smaller and more affordable than Charger 2, though it
          lacks the advanced charging features found on the newer model.
        </p>
        <ImageSet
          size={2}
          shadow
          images={[
            {
              lg: "/images/products/bluetti/charger-2/1920/charger-2_9746.webp",
              sm: "/images/products/bluetti/charger-2/500/charger-2_9746.webp",
              alt: "BLUETTI Charger 2 vs Charger 1 size comparison",
            },
          ]}
        />

        <p>For more details about Charger 2 see my review:</p>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.bluetti.charger2.review} />
          </li>
        </ul>

        <p>
          I would love to see BLUETTI offer a version that keeps the Charger 1
          model's core functionality while adding built‑in D+ support and an
          adjustable charging rate. It would be a great middle ground for smart
          alternator setups, delivering reliable charging without forcing users
          to pay for advanced Charger 2 features they might not need.
        </p>

        <SlugHeading tag="h3">Full Specifications</SlugHeading>
        <Accordion
          items={[
            {
              heading: "Specifications",
              content: (
                <Fragment>
                  <h4>General</h4>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Model:</strong> Charger 1
                    </li>
                    <li>
                      <strong>Net Weight:</strong> 2.6 lbs (1.2 kg)
                    </li>
                    <li>
                      <strong>Dimensions:</strong> 5.7 × 4.3 × 2.4 in (145 × 110
                      × 60 mm)
                    </li>
                    <li>
                      <strong>Noise Level:</strong> 50 dB Max (app includes a
                      "Quiet Mode")
                    </li>
                    <li>
                      <strong>Protection Rating:</strong> IP20
                    </li>
                    <li>
                      <strong>Warranty Period:</strong> 2 years
                    </li>
                  </ul>
                  <hr />
                  <h4>Power</h4>
                  <ul class="none" style="font-size:0.9em;">
                    <li>
                      <strong>Car Battery Input (DC):</strong>
                      <ul>
                        <li>12V, 50A Max. (with 12V battery)</li>
                        <li>24V, 25A Max. (with 24V battery)</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Power Station Output (CHG):</strong> 560W Max,
                      15-56V⎓10A
                      <ul>
                        <li>Default Voltage: 27V</li>
                        <li>
                          Voltage Range (with 12V battery): 15.0V to 56.0V
                          (adjustable via app)
                        </li>
                        <li>
                          Voltage Range (with 24V battery): 27.0V to 56.0V
                          (adjustable via app)
                        </li>
                      </ul>
                    </li>
                    <li>
                      <strong>Breaker Rated Current:</strong> 63A
                    </li>
                    <li>
                      <strong>Protection Type:</strong> Reverse polarity, Over
                      current, Short circuit, Over voltage, Under voltage
                      protection
                    </li>
                  </ul>
                  <hr />
                  <h4>Environmental Temperature</h4>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Operating Temperature:</strong> -4°F to 140°F
                      (-20°C to 60°C)
                    </li>
                    <li>
                      <strong>Storage Temperature:</strong> -4°F to 140°F (-20°C
                      to 60°C)
                    </li>
                    <li>
                      <strong>Operating Humidity:</strong> 10% to 90%
                    </li>
                    <li>
                      <strong>Operating Altitude:</strong> &lt; 6,561 ft (2,000
                      m)
                    </li>
                  </ul>
                  <hr />
                  <h4>Communications</h4>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Bluetooth:</strong> Yes
                    </li>
                  </ul>
                </Fragment>
              ),
            },
            {
              heading: "Cables",
              content: (
                <Fragment>
                  <h4>Included</h4>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Car Battery Cables:</strong> 16.4 ft (5 m), 6 AWG,
                      with 63A breaker
                    </li>
                    <li>
                      <strong>MC4 Output Cable:</strong> 7.87 in (200 mm)
                    </li>
                  </ul>
                </Fragment>
              ),
            },
          ]}
        />

        <SlugHeading tag="h3">Check Compatibility</SlugHeading>
        <p>
          View my{" "}
          <Link href={namedRoutes.bluetti.charger1.compatibility}>
            detailed compatibility guide
          </Link>{" "}
          for {/* confirmed */} BLUETTI power station support and 3rd-party
          battery system limits. My guide outlines the exact wattage limits for
          every BLUETTI power station using Charger 1 alternator charging. It
          also includes a <strong>compatibility calculator</strong> for
          3rd-party brands like EcoFlow, Jackery, and Anker to ensure your setup
          is safe and efficient.
        </p>
        <ReviewItem href={namedRoutes.bluetti.charger1.compatibility} />
      </Center>
      <Center>
        <SlugHeading tag="h2">How to Install</SlugHeading>
        <p>
          Installing Charger 1 isn't difficult, but it does require vehicle
          battery access, making DC electrical connections, thoughtful cable
          routing, and a few basic tools.
        </p>

        <p class="caption-left">Enlarge photos for captions.</p>
        <ImageSet
          size={4}
          maxShow={8}
          border={true}
          showAlt
          images={[
            {
              lg: "/images/products/bluetti/charger-1/install/1920/charger-1_install_9699.webp",
              sm: "/images/products/bluetti/charger-1/install/500/charger-1_install_9699.webp",
              alt: "Connect BAT cable to Charger 1",
            },
            {
              lg: "/images/products/bluetti/charger-1/install/1920/charger-1_install_9702.webp",
              sm: "/images/products/bluetti/charger-1/install/500/charger-1_install_9702.webp",
              alt: "Connect PV cable to Charger 1",
            },
            {
              lg: "/images/products/bluetti/charger-1/install/1920/charger-1_install_9709.webp",
              sm: "/images/products/bluetti/charger-1/install/500/charger-1_install_9709.webp",
              alt: "Connect MC4 adapter cable to PV cable",
            },
            {
              lg: "/images/products/bluetti/charger-2/install/1920/charger-2_install_9625.webp",
              sm: "/images/products/bluetti/charger-2/install/500/charger-2_install_9625.webp",
              alt: "Connect to a portable power station",
            },
            {
              lg: "/images/products/bluetti/charger-2/install/1920/charger-2_install_9663.webp",
              sm: "/images/products/bluetti/charger-2/install/500/charger-2_install_9663.webp",
              alt: "Connect to vehicle battery",
            },
            {
              lg: "/images/products/bluetti/charger-1/install/1920/charger-1_install_9717.webp",
              sm: "/images/products/bluetti/charger-1/install/500/charger-1_install_9717.webp",
              alt: "Ensure DC breaker is in on position",
            },
            {
              lg: "/images/products/bluetti/charger-1/install/1920/charger-1_install_9727.webp?v=2",
              sm: "/images/products/bluetti/charger-1/install/500/charger-1_install_9727.webp?v=2",
              alt: "Setup Charger 1 from BLUETTI app",
            },
          ]}
        />

        <p>
          Because it uses screw terminals, it's important to check them
          occasionally to ensure they don't vibrate loose over time, which can
          be a fire hazard. I appreciate that BLUETTI doesn't use proprietary
          connectors, but it would be nice if they were fully
          vibration-resistant and included additional protections to prevent a
          user from wiring it incorrectly.
        </p>
        <h4>Cables</h4>
        <p>
          For the connection between Charger 1 and your vehicle’s battery,
          BLUETTI utilizes flexible, silicone-insulated 6AWG stranded wire
          encased in a durable braided sleeve. At over 16 feet, the battery
          cable is generously long, and its design allows for significantly
          easier routing compared to traditional cables with rigid PVC
          insulation. In contrast, EcoFlow uses standard PVC insulation and an
          overly tight braided wrap, resulting in a cumbersome cable that is
          much more difficult to maneuver.
        </p>
        <p>
          The included MC4 output cable is very short, but you can easily extend
          it using standard{" "}
          <Link affiliate="https://amzn.to/3OdF2A1">
            solar extension cables
          </Link>
          . Connecting it to your power station requires a separate adapter
          cable (typically MC4 to XT60 or DC8020). While many power stations
          include one, you may need to source it separately depending on your
          setup.
        </p>

        <h4>Need an install guide for a Ford Transit?</h4>
        <ReviewItem href={namedRoutes.bluetti.altCharger.transit} />
      </Center>
      <Center>
        <SlugHeading tag="h3">Setup Videos</SlugHeading>
        <div class={style.videoSplit}>
          <div>
            <div>Install Guide</div>
            <YouTube id="NRyDr1zZ-ws" />
          </div>
          <div>
            <div>App Setup</div>
            <YouTube id="GMas-q21N6s" />
          </div>
        </div>
      </Center>
      <Center>
        <SlugHeading tag="h2">App Control</SlugHeading>
        <p>
          The BLUETTI Charger 1 pairs with the BLUETTI app to allow for device
          monitoring and customization of default settings. The app is available
          on{" "}
          <Link
            external
            href="https://apps.apple.com/us/app/bluetti/id1550568336"
          >
            iOS
          </Link>{" "}
          and{" "}
          <Link
            external
            href="https://play.google.com/store/apps/details?id=net.poweroak.bluetticloud&pli=1"
          >
            Android
          </Link>
          . The following screenshots show all currently available options,
          ordered exactly as they appear in the settings menu.
        </p>

        <ImageSet
          size={6}
          maxShow={6}
          //shadow={true}
          border
          images={[
            {
              lg: "/images/products/bluetti/charger-1/screenshots/lg/20260718_215855000.webp",
              sm: "/images/products/bluetti/charger-1/screenshots/sm/20260718_215855000.webp",
              alt: "",
            },

            {
              lg: "/images/products/bluetti/charger-1/screenshots/lg/20260721_191750000.webp",
              sm: "/images/products/bluetti/charger-1/screenshots/sm/20260721_191750000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-1/screenshots/lg/20260721_190207000.webp",
              sm: "/images/products/bluetti/charger-1/screenshots/sm/20260721_190207000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-1/screenshots/lg/20260718_215947000.webp",
              sm: "/images/products/bluetti/charger-1/screenshots/sm/20260718_215947000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-1/screenshots/lg/20260718_215955000.webp",
              sm: "/images/products/bluetti/charger-1/screenshots/sm/20260718_215955000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-1/screenshots/lg/20260718_220027000.webp",
              sm: "/images/products/bluetti/charger-1/screenshots/sm/20260718_220027000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-1/screenshots/lg/20260718_220035000.webp",
              sm: "/images/products/bluetti/charger-1/screenshots/sm/20260718_220035000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-1/screenshots/lg/20260718_220041000.webp",
              sm: "/images/products/bluetti/charger-1/screenshots/sm/20260718_220041000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-1/screenshots/lg/20260718_220047000.webp",
              sm: "/images/products/bluetti/charger-1/screenshots/sm/20260718_220047000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-1/screenshots/lg/20260718_220052000.webp",
              sm: "/images/products/bluetti/charger-1/screenshots/sm/20260718_220052000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-1/screenshots/lg/20260718_221212000.webp",
              sm: "/images/products/bluetti/charger-1/screenshots/sm/20260718_221212000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-1/screenshots/lg/20260718_220619000.webp",
              sm: "/images/products/bluetti/charger-1/screenshots/sm/20260718_220619000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-1/screenshots/lg/20260718_220624000.webp",
              sm: "/images/products/bluetti/charger-1/screenshots/sm/20260718_220624000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-1/screenshots/lg/20260718_220634000.webp",
              sm: "/images/products/bluetti/charger-1/screenshots/sm/20260718_220634000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-1/screenshots/lg/20260718_220639000.webp",
              sm: "/images/products/bluetti/charger-1/screenshots/sm/20260718_220639000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-1/screenshots/lg/20260718_220659000.webp",
              sm: "/images/products/bluetti/charger-1/screenshots/sm/20260718_220659000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-1/screenshots/lg/20260718_220702000.webp",
              sm: "/images/products/bluetti/charger-1/screenshots/sm/20260718_220702000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-1/screenshots/lg/20260718_220706000.webp",
              sm: "/images/products/bluetti/charger-1/screenshots/sm/20260718_220706000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-1/screenshots/lg/20260718_220710000.webp",
              sm: "/images/products/bluetti/charger-1/screenshots/sm/20260718_220710000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-1/screenshots/lg/20260718_220839000.webp",
              sm: "/images/products/bluetti/charger-1/screenshots/sm/20260718_220839000.webp",
              alt: "",
            },
          ]}
        />

        <p class="caption-left">
          The app displays the device serial number on multiple main screens
          rather than a single settings page. This makes it far too easy to
          accidentally leak private hardware info and made capturing clean,
          shareable screenshots a chore.
        </p>

        <p>
          With a few small improvements, the app could be drastically improved:
        </p>
        <ul>
          <li>
            <strong>App design:</strong> BLUETTI’s app would benefit from
            clearer organization. However, the Charger 1 app experience is much
            more straightforward than that of Charger 2.
          </li>
          <li>
            <strong>In-App Guidance:</strong> While the existing tooltips are a
            great start for understanding complex settings, the app would
            benefit from more consistent coverage. Currently, several key
            features lack any explanation, which forces users to make
            assumptions about how a setting might impact their system. Adding
            tooltips for every toggle and including short, "how-to" video
            snippets in the guides section would go a long way in making the app
            feel like a complete resource for the user.
          </li>
          <li>
            <strong>Adjustable charging:</strong> The app lets you adjust the
            voltage but not the charging rate (amperage). Even if an alternator
            supports 560W, users need the ability to dial it back on different
            trips to manage heat or efficiency.
          </li>
          <li>
            <strong>Vehicle Integration:</strong> The lack of CarPlay and
            Android Auto support is a missed opportunity for a device often used
            in van-life and overlanding setups where hands-free monitoring is
            important.
          </li>
        </ul>
      </Center>

      <Center>
        <SlugHeading tag="h2">My Verdict</SlugHeading>
        <p>
          The <strong>BLUETTI Charger 1</strong> is an easy-to-use, reliable
          DC-DC charger that delivers up to <strong>560W</strong> of power while
          driving. While higher-wattage units exist, its compact footprint makes
          it easy to mount in tight setups. Its primary drawback is that it
          occupies your power station’s solar input port rather than connecting
          through a dedicated expansion battery port like Charger 2 supports.
          Overall, it’s a capable and competitive alternator charger option.
        </p>
        <p>
          <strong>The Bottom Line:</strong> If you are already in the BLUETTI
          ecosystem and want reliable on-the-go charging without paying extra
          for Charger 2’s advanced features, Charger 1 is a solid,
          straightforward hardware addition.
        </p>

        <BluettiCouponCode
          mini
          href="https://www.bluettipower.com/products/bluetti-charger-1"
          buttonText="Buy from BLUETTI"
        />

        <p>
          Not sure it’s the best fit? My{" "}
          <Link href={namedRoutes.alternatorChargers}>
            alternator charger guide
          </Link>{" "}
          covers all the popular brands.
        </p>

        <ButtonList>
          <Button
            affiliateData={affiliateMerchants.bluetti}
            href="https://www.bluettipower.com/products/bluetti-charger-1"
            icon="Bluetti"
          >
            Buy from BLUETTI
          </Button>
          <Button
            affiliate="https://amzn.to/4msoVLk"
            href="https://www.amazon.com/dp/B0DGXYPMQ4/"
            secondary
            icon="Amazon"
          >
            Buy from Amazon
          </Button>
        </ButtonList>

        <p class="caption-left">
          This page will be updated as new information becomes available.
        </p>

        <Tags tags={headerInfo.tags} brands={headerInfo.brands} />
      </Center>
      <Center>
        <h3>Related</h3>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.alternatorChargers} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.bluetti.altCharger.transit} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.inventory.vanlife} />
          </li>
        </ul>
      </Center>
      <Questions />
      <Center>
        <Author
          author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />
      </Center>
      <StickyButtons
        title="BLUETTI Charger 1"
        description={
          <Fragment>
            $249 <s>$399</s>
          </Fragment>
        }
      >
        <Button
          affiliateData={affiliateMerchants.bluetti}
          href="https://www.bluettipower.com/products/bluetti-charger-1"
          icon="Bluetti"
        >
          View on BLUETTI
        </Button>
        <Button
          affiliate="https://amzn.to/4msoVLk"
          href="https://www.amazon.com/dp/B0DGXYPMQ4/"
          secondary
          icon="Amazon"
        >
          View on Amazon
        </Button>
      </StickyButtons>
    </Editorial>
  </div>
);

export default Page;
